import { FC } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  ButtonBase,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Colors } from "../../../configs";
import { pxToRem } from "../../../util";

type Props = {
  open: boolean;
  onClose: () => void;
  onSelectRetail: () => void;
  onSelectBusiness: () => void;
};

const CustomerTypeModal: FC<Props> = ({
  open,
  onClose,
  onSelectRetail,
  onSelectBusiness,
}) => {
  const options = [
    {
      title: "Business Customer",
      subtitle: "Billed accounts, organizations, and commercial service",
      icon: <BusinessIcon sx={{ fontSize: pxToRem(26), color: "#fff" }} />,
      accent: Colors.SECONDARY,
      onClick: onSelectBusiness,
      comingSoon: true,
    },
    {
      title: "Retail Customer",
      subtitle: "Walk-in customers and personal garment care",
      icon: <PersonOutlineIcon sx={{ fontSize: pxToRem(26), color: "#fff" }} />,
      accent: Colors.PRIMARY,
      onClick: onSelectRetail,
      comingSoon: false,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: pxToRem(560),
          maxWidth: "92vw",
          borderRadius: pxToRem(22),
          boxShadow: "0 24px 70px rgba(0,0,0,0.10)",
          border: "1px solid rgba(0,0,0,0.05)",
          overflow: "hidden",
          backgroundColor: Colors.WHITE,
        },
      }}
    >
      <DialogContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        {/* Header */}
        <Box
          sx={{
            px: pxToRem(28),
            pt: pxToRem(26),
            pb: pxToRem(20),
            borderBottom: "1px solid #E8E8E8",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: pxToRem(11),
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.22em",
                color: Colors.SECONDARY,
                lineHeight: 1.3,
              }}
            >
              Account Setup
            </Typography>
            <Typography
              sx={{
                mt: pxToRem(6),
                fontSize: pxToRem(28),
                fontWeight: 600,
                letterSpacing: "-0.02em",
                color: Colors.TEXT_GRAY_DARK,
                lineHeight: 1.1,
              }}
            >
              Customer Type
            </Typography>
            <Typography
              sx={{
                mt: pxToRem(8),
                fontSize: pxToRem(13),
                color: "rgba(81,81,81,0.65)",
              }}
            >
              Select the customer profile to continue.
            </Typography>
          </Box>

          <ButtonBase
            onClick={onClose}
            sx={{
              width: pxToRem(40),
              height: pxToRem(40),
              borderRadius: "50%",
              border: "1px solid #E0E0E0",
              color: Colors.TEXT_GRAY_DARK,
              flexShrink: 0,
              transition: "background-color 0.15s ease",
              "&:hover": { backgroundColor: "#F5F5F5" },
            }}
          >
            <CloseIcon sx={{ fontSize: pxToRem(18) }} />
          </ButtonBase>
        </Box>

        {/* Options */}
        <Box
          sx={{
            p: pxToRem(20),
            display: "flex",
            flexDirection: "column",
            gap: pxToRem(12),
            backgroundColor: Colors.BACKGROUND_GRAY,
          }}
        >
          {options.map((opt) => (
            <ButtonBase
              key={opt.title}
              onClick={opt.comingSoon ? undefined : opt.onClick}
              disabled={opt.comingSoon}
              sx={{
                width: "100%",
                borderRadius: pxToRem(16),
                border: "1px solid #E0E0E0",
                backgroundColor: Colors.WHITE,
                px: pxToRem(18),
                py: pxToRem(18),
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: pxToRem(16),
                transition: "all 0.18s ease",
                opacity: opt.comingSoon ? 0.55 : 1,
                cursor: opt.comingSoon ? "not-allowed" : "pointer",
                "&:hover:not(:disabled)": {
                  borderColor: `${opt.accent}55`,
                  backgroundColor: `${opt.accent}07`,
                  boxShadow: "0 12px 28px rgba(0,0,0,0.06)",
                },
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  width: pxToRem(48),
                  height: pxToRem(48),
                  borderRadius: pxToRem(14),
                  backgroundColor: opt.accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                }}
              >
                {opt.icon}
              </Box>

              {/* Text */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: pxToRem(12),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: pxToRem(17),
                      fontWeight: 600,
                      letterSpacing: "-0.02em",
                      color: Colors.TEXT_GRAY_DARK,
                      lineHeight: 1.25,
                    }}
                  >
                    {opt.title}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: pxToRem(6) }}>
                    {opt.comingSoon && (
                      <Box
                        sx={{
                          px: pxToRem(8),
                          py: pxToRem(3),
                          borderRadius: pxToRem(6),
                          backgroundColor: "#F3F4F6",
                          border: "1px solid #E5E7EB",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: pxToRem(10),
                            fontWeight: 600,
                            color: "#9CA3AF",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                          }}
                        >
                          Coming Soon
                        </Typography>
                      </Box>
                    )}
                    {!opt.comingSoon && (
                      <ArrowForwardIcon
                        sx={{ fontSize: pxToRem(18), color: "rgba(81,81,81,0.3)" }}
                      />
                    )}
                  </Box>
                </Box>
                <Typography
                  sx={{
                    mt: pxToRem(3),
                    fontSize: pxToRem(12),
                    color: "rgba(81,81,81,0.6)",
                    lineHeight: 1.45,
                  }}
                >
                  {opt.subtitle}
                </Typography>
              </Box>
            </ButtonBase>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerTypeModal;
